import React, { useState, useEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import {
  Button,
  ButtonToolbar,
  Form,
  Input,
  Panel,
  SelectPicker,
  Notification,
  toaster,
  Divider
} from 'rsuite'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import AppLoader from '@components/common/AppLoader'

import { ROUTE_SETTINGS_TOPOLOGY } from '@@partner:config/routes'

import { useFetchTopology, TopologyRegion } from '@@partner:graphql/query/topology.gql'
import { useSaveRegion, RegionInput, TranslationInput } from '@@partner:graphql/mutation/topology/saveRegion.gql'

interface RegionFormPageParams {
  regionId?: string
}

interface FormData {
  id?: string
  name: string
  country_id: string
  i18n: TranslationInput[]
}

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  country_id: '',
  i18n: []
}

const LANGUAGES = [
  { code: 'ru', label: 'Русский' },
  { code: 'kk', label: 'Казахский' },
  { code: 'en', label: 'English' }
]

export default function RegionFormPage () {
  const history = useHistory()
  const { regionId } = useParams<RegionFormPageParams>()
  const isEditing = !!regionId

  const { data: topologyData, loading: topologyLoading } = useFetchTopology()
  const [ saveRegion, { loading: saving } ] = useSaveRegion()

  const [ formData, setFormData ] = useState<FormData>(DEFAULT_FORM_DATA)

  const countryOptions = useMemo(
    () => topologyData?.topology?.countries?.map(c => ({ label: c.name, value: c.id })) ?? [],
    [ topologyData ]
  )

  useEffect(() => {
    if (isEditing && topologyData?.topology?.countries) {
      // Find region in topology data
      for (const country of topologyData.topology.countries) {
        const region = country.regions?.find(r => r.id === regionId)
        if (region) {
          setFormData({
            id: region.id,
            name: region.name,
            country_id: region.country_id,
            i18n: region.i18n ?? []
          })
          break
        }
      }
    }
  }, [ isEditing, regionId, topologyData ])

  const handleTranslationChange = (langCode: string, name: string) => {
    const newI18n = [ ...formData.i18n ]
    const existingIndex = newI18n.findIndex(t => t.language_code === langCode)
    
    if (existingIndex >= 0) {
      newI18n[existingIndex] = { language_code: langCode, name }
    }
    else {
      newI18n.push({ language_code: langCode, name })
    }
    
    setFormData({ ...formData, i18n: newI18n })
  }

  const getTranslation = (langCode: string) => {
    return formData.i18n.find(t => t.language_code === langCode)?.name ?? ''
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.country_id) {
      toaster.push(
        <Notification type="error" header="Ошибка">Заполните обязательные поля (Название, Страна)</Notification>,
        { placement: 'topEnd' }
      )
      return
    }

    try {
      const regionInput: RegionInput = {
        name: formData.name,
        country_id: formData.country_id,
        i18n: formData.i18n.filter(t => t.name)
      }

      if (isEditing && formData.id) {
        regionInput.id = formData.id
      }

      await saveRegion({
        variables: {
          regionInput,
          upsert: !isEditing
        }
      })

      toaster.push(
        <Notification type="success" header="Успех">
          Регион {isEditing ? 'обновлён' : 'создан'}
        </Notification>,
        { placement: 'topEnd' }
      )

      history.push(ROUTE_SETTINGS_TOPOLOGY)
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }

  if (topologyLoading && isEditing) {
    return <AppLoader />
  }

  return (
    <div className="RegionFormPage">
      <h1 className="u-heading">{isEditing ? 'Редактирование региона' : 'Создание региона'}</h1>
      <SettingsTopMenu />

      <Panel shaded className="mt-4">
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Страна *</Form.ControlLabel>
            <SelectPicker
              data={countryOptions}
              value={formData.country_id}
              onChange={(value) => setFormData({ ...formData, country_id: value ?? '' })}
              placeholder="Выберите страну"
              block
              searchable
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Название (по умолчанию) *</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              placeholder="Алматинская область"
            />
          </Form.Group>

          <Divider>Переводы</Divider>

          {LANGUAGES.map(lang => (
            <Form.Group key={lang.code}>
              <Form.ControlLabel>Название ({lang.label})</Form.ControlLabel>
              <Input
                value={getTranslation(lang.code)}
                onChange={(value) => handleTranslationChange(lang.code, value)}
                placeholder={`Название на ${lang.label.toLowerCase()}`}
              />
            </Form.Group>
          ))}

          <ButtonToolbar className="mt-6">
            <Button
              appearance="primary"
              onClick={handleSubmit}
              loading={saving}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
            <Button
              appearance="subtle"
              onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY)}
            >
              Отмена
            </Button>
          </ButtonToolbar>
        </Form>
      </Panel>
    </div>
  )
}
